// Loan Financials — Fees ledger. Create / edit a standalone LoanFeeEntry.
// Entry + tracking only (no waterfall wiring). The RevenuePool tag is
// optional: its option list comes from the commissions pool endpoint, which
// gates on a different permission, so a 403/empty result just leaves the
// select at "None" and the fee saves without a pool.

import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Input, Label, Textarea } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { commissionsApi } from "@/api/commissions";
import {
  PAYMENT_CHANNELS,
  type LoanFeeDto,
} from "@/api/portfolioAdmin/loanFeesApi";
import {
  useCreateLoanFee,
  useUpdateLoanFee,
} from "@/hooks/portfolioAdmin/useLoanFees";
import { loanFeesKeys } from "@/hooks/portfolioAdmin/loanFeesKeys";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

const SELECT_CLASS =
  "w-full border border-border-light rounded-md px-space-2 py-space-1 text-small bg-white-pure";

/** ISO instant → `YYYY-MM-DD` for a date input (or today, in the viewer's zone). */
function toDateInput(iso: string | null | undefined, tz: string): string {
  if (!iso) return isoDateInTz(new Date(), tz);
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? isoDateInTz(new Date(), tz)
    : d.toISOString().slice(0, 10);
}

export function LoanFeeEntryModal({
  loanId,
  open,
  onClose,
  fee,
}: {
  loanId: string;
  open: boolean;
  onClose: () => void;
  fee?: LoanFeeDto;
}) {
  const isEdit = !!fee;
  const create = useCreateLoanFee(loanId);
  const update = useUpdateLoanFee(loanId);
  const tz = useUserTimeZone();

  // Optional pool options — graceful when the caller lacks Claims.Read.
  const poolsQuery = useQuery({
    queryKey: loanFeesKeys.poolOptions(),
    queryFn: () => commissionsApi.listPools(true),
    enabled: open,
    retry: false,
  });
  const pools = useMemo(() => poolsQuery.data ?? [], [poolsQuery.data]);

  const [feeType, setFeeType] = useState("");
  const [amount, setAmount] = useState("");
  const [payer, setPayer] = useState("");
  const [paymentChannel, setPaymentChannel] = useState<number>(PAYMENT_CHANNELS[0].value);
  const [revenuePoolId, setRevenuePoolId] = useState<string>("");
  const [incurredDate, setIncurredDate] = useState(toDateInput(null, tz));
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Re-seed the form whenever the modal opens (or switches between
  // create / a specific fee).
  useEffect(() => {
    if (!open) return;
    setError(null);
    setFeeType(fee?.feeType ?? "");
    setAmount(fee ? fee.amount.toFixed(2) : "");
    setPayer(fee?.payer ?? "");
    setPaymentChannel(fee?.paymentChannel ?? PAYMENT_CHANNELS[0].value);
    setRevenuePoolId(fee?.revenuePoolId ?? "");
    setIncurredDate(toDateInput(fee?.incurredDate, tz));
    setDescription(fee?.description ?? "");
    setNotes(fee?.notes ?? "");
  }, [open, fee, tz]);

  const pending = create.isPending || update.isPending;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (feeType.trim().length === 0) {
      setError("Fee type is required.");
      return;
    }
    if (payer.trim().length === 0) {
      setError("Payer is required.");
      return;
    }
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setError("Enter a non-negative amount.");
      return;
    }
    const incurredIso = new Date(`${incurredDate}T00:00:00Z`).toISOString();
    try {
      if (isEdit && fee) {
        await update.mutateAsync({
          feeId: fee.id,
          body: {
            feeType: feeType.trim(),
            description: description.trim() || "",
            amount: parsed,
            revenuePoolId: revenuePoolId || null,
            // Deselecting the pool ("None") must clear it, not leave it unchanged.
            clearRevenuePool: !revenuePoolId,
            paymentChannel,
            payer: payer.trim(),
            notes: notes.trim() || "",
          },
        });
      } else {
        await create.mutateAsync({
          feeType: feeType.trim(),
          description: description.trim() || null,
          amount: parsed,
          revenuePoolId: revenuePoolId || null,
          paymentChannel,
          payer: payer.trim(),
          incurredDate: incurredIso,
          notes: notes.trim() || null,
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "The fee could not be saved.");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit fee" : "Add fee"}
      description="Records a loan cost. Borrower-payable fees go to B3 review — once approved they bill on the next borrower statement and collect through the waterfall. Fees payable by others are recorded for tracking only."
    >
      <form onSubmit={submit} className="flex flex-col gap-space-3" data-testid="fee-entry-form">
        <div className="grid grid-cols-2 gap-space-3">
          <div>
            <Label htmlFor="fee-type">Fee type</Label>
            <Input
              id="fee-type"
              required
              autoFocus
              value={feeType}
              onChange={(e) => setFeeType(e.target.value)}
              placeholder="e.g. Late fee, Legal cost"
              data-testid="fee-type"
            />
          </div>
          <div>
            <Label htmlFor="fee-amount">Amount</Label>
            <Input
              id="fee-amount"
              type="number"
              step="0.01"
              min="0"
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="fee-amount"
            />
          </div>
          <div>
            <Label htmlFor="fee-payer">Payer</Label>
            <Input
              id="fee-payer"
              required
              value={payer}
              onChange={(e) => setPayer(e.target.value)}
              placeholder="e.g. Borrower, Seller, Phoenix"
              data-testid="fee-payer"
            />
          </div>
          <div>
            <Label htmlFor="fee-channel">Payment channel</Label>
            <select
              id="fee-channel"
              className={SELECT_CLASS}
              value={paymentChannel}
              onChange={(e) => setPaymentChannel(Number(e.target.value))}
              data-testid="fee-channel"
            >
              {PAYMENT_CHANNELS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="fee-pool">Revenue pool</Label>
            <select
              id="fee-pool"
              className={SELECT_CLASS}
              value={revenuePoolId}
              onChange={(e) => setRevenuePoolId(e.target.value)}
              data-testid="fee-pool"
            >
              <option value="">None</option>
              {pools.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} — {p.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="fee-incurred">Incurred date</Label>
            <Input
              id="fee-incurred"
              type="date"
              required
              disabled={isEdit}
              value={incurredDate}
              onChange={(e) => setIncurredDate(e.target.value)}
              data-testid="fee-incurred"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="fee-description">Description</Label>
          <Input
            id="fee-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
            data-testid="fee-description"
          />
        </div>
        <div>
          <Label htmlFor="fee-notes">Notes</Label>
          <Textarea
            id="fee-notes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional"
            data-testid="fee-notes"
          />
        </div>
        {error && (
          <p className="text-small text-status-error m-0" role="alert" data-testid="fee-error">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-space-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={pending} data-testid="fee-submit">
            {pending ? "Saving…" : isEdit ? "Save changes" : "Add fee"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
